import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Alert, Button, Result, Spin } from "antd";
import { FormattedMessage } from "react-intl";
import { loadConnectAndInitialize } from "@stripe/connect-js";
import {
  ConnectAccountManagement,
  ConnectComponentsProvider,
  ConnectDocuments,
} from "@stripe/react-connect-js";
import Header from "../../components/Header/Header";
import { BackButton } from "../../components/General/BackButton";
import { createTaxSession } from "../../api/stripeApi/taxSession";
import { readAuthToken } from "../../utils/axiosMiddleware";
import { stripQueryParam } from "../../utils/stripeRedirect";
import {
  isHandoffSession,
  isWebViewSession,
  markHandoffSession,
  markWebViewSession,
} from "../../utils/webViewSession";
import { returnToApp } from "../../utils/appDeepLink";
import { ROUTES } from "../../constants/routes";
import "./TaxDocuments.scss";
import {
  canRetry,
  getTaxCopy,
  needsOpenApp,
  needsPayoutSetup,
  needsSignIn,
  resolveTaxState,
  shouldRequestSession,
  TAX_STATES,
} from "./taxDocumentsState";
import { createOneShotSecret } from "./oneShotSecret";

/**
 * 1099 tax documents (`/stripe/tax-documents`).
 *
 * Two Stripe embedded components: Account management, where a gigster confirms
 * their tax details and consents to paperless delivery, and Documents, where
 * the form itself is downloaded once filed in January. Stripe's pre-filing
 * emails link here from November, so the page has to be useful months before
 * any form exists — hence Account management first, Documents below it showing
 * its own empty state.
 *
 * ⚠️ **Not usable inside a mobile WebView.** Stripe: *"You can't use Connect
 * embedded components in embedded web views inside mobile or desktop
 * applications… If an embedded component isn't supported yet by our mobile
 * SDKs, we recommend linking to a web browser."* Neither Account management nor
 * Documents exists in the RN SDK at any version, so mobile must open this page
 * in the **system browser** (`Linking.openURL`), the way payout onboarding
 * already does — not in `CustomWebView`.
 *
 * The system browser has no localStorage session, so the mobile app opens this
 * page with `?code=`, a one-time handoff code from `POST stripe/connect/
 * tax-handoff`. The code is redeemed straight into an account session — it is
 * never turned into a bearer token — and is stripped from the URL at once.
 * Being single-use, it cannot be replayed, so a reload or a Stripe session
 * refresh lands on SESSION_EXPIRED and sends the user back to the app.
 *
 * `?token=` (the older WebView hand-off) and a plain localStorage session still
 * work. The token is threaded into the session request explicitly; without it
 * the call 401s and the interceptor clears storage and redirects home.
 */
export const TaxDocuments = () => {
  const searchParams = useMemo(
    () => new URLSearchParams(window.location.search),
    []
  );
  const urlToken = searchParams.get("token");
  const handoffCode = searchParams.get("code");
  // Stripe appends this to the links in the emails it sends a connected
  // account. Used only to confirm the right person followed the link — the
  // session is always minted for whoever is signed in, never for an account id
  // taken from a URL.
  const linkedAccountId = searchParams.get("stripe_account_id");
  const authToken = readAuthToken();
  const accessToken = urlToken || authToken?.accessToken;
  // Reloading after the code is stripped leaves a signed-out page that the
  // sign-in prompt would misdescribe; the marker says this tab came from the
  // app. A real session overrides it.
  const hadHandoff = isHandoffSession();
  const [fellBackToSession, setFellBackToSession] = useState(false);
  const handoff =
    !fellBackToSession &&
    (Boolean(handoffCode) || (hadHandoff && !accessToken));
  // Inside the WebView the app supplies its own chrome, and the token is gone
  // after any Stripe round trip — so the marker is persisted for the return.
  const isWebView = isWebViewSession(urlToken);

  const hideChrome = isWebView || handoff;

  const [session, setSession] = useState(null);
  const [failed, setFailed] = useState(false);
  const [statusLoaded, setStatusLoaded] = useState(false);
  const [codeRejected, setCodeRejected] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [retryable, setRetryable] = useState(false);
  const [throttled, setThrottled] = useState(false);
  // StrictMode runs mount effects twice in development; without this the page
  // opens two account sessions for one visit.
  const requestedRef = useRef(false);

  const oneShotSecret = useMemo(
    () => createOneShotSecret(() => setSessionExpired(true)),
    []
  );

  const applySession = useCallback(
    (data, viaHandoff) => {
      const usable = data?.noPayoutAccount || data?.clientSecret;
      if (!usable) {
        setFailed(true);
        return;
      }
      if (viaHandoff) {
        oneShotSecret.store(data.clientSecret);
      }
      setSession(data);
    },
    [oneShotSecret]
  );

  const requestSession = useCallback(async () => {
    setFailed(false);
    setRetryable(false);
    setThrottled(false);
    const data = await createTaxSession(
      urlToken || undefined,
      handoffCode || undefined
    );
    setStatusLoaded(true);
    if (data?.retryable) {
      setFailed(true);
      setRetryable(true);
      setThrottled(Boolean(data.throttled));
      return;
    }
    const staleLinkWithSession =
      Boolean(handoffCode) && data?.codeRejected && Boolean(accessToken);
    if (handoffCode) {
      stripQueryParam("code");
      if (!staleLinkWithSession) {
        markHandoffSession();
      }
    }
    if (staleLinkWithSession) {
      setFellBackToSession(true);
      const sessionData = await createTaxSession(urlToken || undefined);
      applySession(sessionData, false);
      return;
    }
    if (data?.codeRejected) {
      console.warn("Tax page: the handoff code was refused");
      setCodeRejected(true);
      return;
    }
    applySession(data, Boolean(handoffCode));
  }, [urlToken, handoffCode, accessToken, applySession]);

  useEffect(() => {
    if (urlToken) {
      markWebViewSession();
    }
  }, [urlToken]);

  const state = resolveTaxState({
    signedIn: Boolean(accessToken || handoffCode),
    codeRejected,
    sessionExpired,
    handoffLost:
      !fellBackToSession && hadHandoff && !handoffCode && !accessToken,
    statusLoaded,
    hasPayoutAccount: session ? !session.noPayoutAccount : undefined,
    isPayee: session?.isPayee ?? true,
    clientSecret: session?.clientSecret,
    accountId: session?.accountId,
    linkedAccountId,
    failed,
    throttled,
  });
  const { title, description } = getTaxCopy(state);

  // `shouldRequestSession` rather than an inline `!accessToken` check: the
  // resolver already encodes which states may fetch, and a second copy of that
  // rule here is one that can drift from the one the tests assert on.
  useEffect(() => {
    if (!shouldRequestSession(state) || requestedRef.current) {
      return;
    }
    requestedRef.current = true;
    requestSession();
  }, [state, requestSession]);

  const retry = useCallback(() => {
    requestedRef.current = true;
    requestSession();
  }, [requestSession]);

  // One Connect instance per session, reused by both components — Stripe calls
  // creating one per component a common mistake.
  //
  // `fetchClientSecret` mints a NEW session each call. Stripe also uses it to
  // refresh an expired session ("should always create a new account session and
  // return a fresh client_secret"), so handing back the cached one would make
  // the refresh path a silent no-op — and Account management is exactly the
  // page someone leaves open while they hunt for their TIN.
  const hasSession = Boolean(session?.clientSecret);
  const connectInstance = useMemo(() => {
    if (!hasSession) {
      return null;
    }
    return loadConnectAndInitialize({
      publishableKey: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY,
      fetchClientSecret:
        handoffCode && !fellBackToSession
          ? oneShotSecret.fetchClientSecret
          : async () =>
              (await createTaxSession(urlToken || undefined))?.clientSecret,
    });
  }, [hasSession, urlToken, handoffCode, fellBackToSession, oneShotSecret]);

  // Carry the current URL — including Stripe's `stripe_account_id` — through
  // sign-in, the same shape `RedirectHome` uses for protected routes. Nothing
  // consumes `next` yet, so today this only preserves the destination in the
  // URL rather than returning the user automatically; wiring that up is a
  // separate change to the sign-in flow.
  //
  // `token` is dropped first (SEC-4). In the WebView case the search string
  // holds the raw JWT, and the sign-out action clears localStorage and then
  // navigates here — re-encoding a still-valid token into a fresh history
  // entry is exactly what `stripSensitiveQueryParams` exists to prevent.
  const signInHref = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    params.delete("token");
    params.delete("code");
    const query = params.toString();
    return `/login?next=${encodeURIComponent(
      `${window.location.pathname}${query ? `?${query}` : ""}`
    )}`;
  }, []);

  const actions = [
    needsSignIn(state) && (
      <Button
        key="signin"
        type="primary"
        className="fw-bold"
        href={signInHref}
        data-testid="tax-sign-in"
      >
        <FormattedMessage id="SIGN_IN_TO_CONTINUE" />
      </Button>
    ),
    needsPayoutSetup(state, handoff) && (
      <Button
        key="setup"
        type="primary"
        className="fw-bold"
        href={ROUTES.STRIPE_ONBOARDING}
        data-testid="tax-setup-payouts"
      >
        <FormattedMessage id="SET_UP_PAYOUTS" />
      </Button>
    ),
    /* NOT_A_PAYEE is the only state with nothing to do next, and in the
       WebView there is no Header or BackButton either — so without this the
       page is terminal with no way off it. Same escape StripeRefresh offers
       when the web has nothing useful for someone who came from the app. */
    ((isWebView && state === TAX_STATES.NOT_A_PAYEE) ||
      needsOpenApp(state, handoff, retryable)) && (
      <Button
        key="app"
        type="primary"
        className="fw-bold"
        onClick={() => returnToApp(handoff ? undefined : authToken?.user_id)}
        data-testid="tax-open-app"
      >
        <FormattedMessage id="OPEN_GIGMI_APP" />
      </Button>
    ),
    canRetry(state, handoff, retryable) && (
      <Button
        key="retry"
        type="primary"
        className="fw-bold"
        onClick={retry}
        data-testid="tax-retry"
      >
        <FormattedMessage id="TRY_AGAIN" />
      </Button>
    ),
    /* The copy tells them to sign back in as the account the email was sent
       to, so give them the way to do it rather than leaving them to find it.
       Same clear-and-redirect as Settings' sign-out. */
    state === TAX_STATES.WRONG_ACCOUNT && (
      <Button
        key="switch"
        type="primary"
        className="fw-bold"
        onClick={() => {
          localStorage.clear();
          window.location.href = signInHref;
        }}
        data-testid="tax-switch-account"
      >
        <FormattedMessage id="header.signout" />
      </Button>
    ),
  ].filter(Boolean);

  if (state !== TAX_STATES.READY) {
    return (
      <>
        {!hideChrome && <Header />}
        <div className="container mt-4">
          {!hideChrome && <BackButton />}
          <Result
            status={state === TAX_STATES.FAILED ? "warning" : "info"}
            icon={
              state === TAX_STATES.LOADING ? <Spin size="large" /> : undefined
            }
            title={<FormattedMessage id={title} />}
            subTitle={<FormattedMessage id={description} />}
            extra={actions.length ? actions : undefined}
          />
        </div>
      </>
    );
  }

  return (
    <>
      {!hideChrome && <Header />}
      <div className="container mt-4">
        {!hideChrome && <BackButton />}
        <h4 className="fw-bold mt-3">
          <FormattedMessage id={title} />
        </h4>
        <p className="text-muted">
          <FormattedMessage id={description} />
        </p>
        {/* Stripe cannot e-deliver without an email on the connected account,
            so say so rather than showing a consent control that does nothing. */}
        {session?.hasEmail === false && (
          <Alert
            type="warning"
            showIcon
            className="mb-3"
            message={<FormattedMessage id="TAX_NO_EMAIL_WARNING" />}
            data-testid="tax-no-email-warning"
          />
        )}
        <ConnectComponentsProvider connectInstance={connectInstance}>
          <section className="mb-4">
            <h5 className="fw-bold">
              <FormattedMessage id="TAX_YOUR_DETAILS" />
            </h5>
            <ConnectAccountManagement />
          </section>
          <section className="mb-5">
            <h5 className="fw-bold">
              <FormattedMessage id="TAX_YOUR_FORMS" />
            </h5>
            <ConnectDocuments />
          </section>
        </ConnectComponentsProvider>
      </div>
    </>
  );
};

export default TaxDocuments;
